import { useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { PROMOTION_DEFAULT_GOAL_ID } from '../../../../constant';
import { loadDirectMetricsAsync } from '../../../../data-layer/direct/actions';
import { selectMetrics } from '../../../../data-layer/direct/selectors';
import {
    DirectCampaignUI,
    DirectGoalCostType,
    DirectPromotionGoalUI,
    DirectStrategyType,
} from '../../../../data-layer/direct/types';
import { useDispatch } from '../../../../data-layer/useDispatch';
import { useSelector } from '../../../../data-layer/useSelector';

const defaultGoal = { [PROMOTION_DEFAULT_GOAL_ID]: { cost: 1 } };

function getSelectedGoals(company?: DirectCampaignUI) {
    if (!company) {
        return {};
    }

    const { counters } = company;

    if (!counters.length && company.strategyType === DirectStrategyType.MAX_CLICKS) {
        return defaultGoal;
    }

    return counters.reduce<DirectPromotionGoalUI>((res, counter) => {
        const selected = counter.goals.reduce<DirectPromotionGoalUI>(
            (goals, goal) => ({
                ...goals,
                [String(goal.goal_id)]: {
                    cost: goal.cost,
                    costType:
                        goal.cost || !goal.is_metrika_source_of_value
                            ? DirectGoalCostType.MANUAL
                            : DirectGoalCostType.DYNAMIC,
                },
            }),
            {},
        );

        return {
            ...res,
            ...selected,
        };
    }, {});
}

function getMetricsSettings(company?: DirectCampaignUI) {
    const { counters, weeklyLimit } = company || {};

    return {
        selectedCounters:
            counters?.map((counter) => String(counter.counter_id)).filter(Boolean) ?? [],
        selectedGoals: getSelectedGoals(company),
        weeklyLimit,
    };
}

export const usePromotionMetrics = (
    company?: DirectCampaignUI,
    strategyType?: DirectStrategyType,
) => {
    const dispatch = useDispatch();
    const metrics = useSelector((state) => selectMetrics(state));
    const onLoadMetrics = useCallback(() => dispatch(loadDirectMetricsAsync()), [dispatch]);

    const metricsSettings = getMetricsSettings(company);

    const isRequiredDefaultGoal = strategyType === DirectStrategyType.MAX_CLICKS;

    const [selectedCounters, setSelectedCounters] = useState<string[]>(
        metricsSettings.selectedCounters,
    );
    const [selectedGoals, setSelectedGoals] = useState<DirectPromotionGoalUI>(
        metricsSettings.selectedGoals,
    );

    const onSelectCounter = useCallback(
        (key: string) => {
            if (selectedCounters.indexOf(key) !== -1) {
                return;
            }

            setSelectedCounters([...selectedCounters, key]);
        },
        [selectedCounters],
    );
    const onRemoveCounter = useCallback(
        (key: string) => {
            const newSelectedMetrics = selectedCounters.filter((seleced) => seleced !== key);

            setSelectedCounters(newSelectedMetrics);

            if (isRequiredDefaultGoal && newSelectedMetrics.length === 0) {
                setSelectedGoals(defaultGoal);
            } else {
                const goalsIdsForRemove = metrics.goals
                    .filter((goal) => goal.counterId === key)
                    .map((goal) => goal.id);

                const newSelectedGoals = { ...selectedGoals };
                goalsIdsForRemove.forEach((goalId) => {
                    delete newSelectedGoals[goalId];
                });

                setSelectedGoals(newSelectedGoals);
            }
        },
        [selectedCounters, isRequiredDefaultGoal, metrics.goals, selectedGoals],
    );

    const onSelectGoals = useCallback(
        (selected: string[]) => {
            const newSelectedGoals = selected.reduce(
                (res, id) => ({
                    ...res,
                    [id]: selectedGoals?.[id] || { costType: DirectGoalCostType.DYNAMIC },
                }),
                {},
            );

            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals],
    );
    const onChangeDefaultGoal = useCallback(
        (isSelected: boolean) => {
            if (isSelected && Object.keys(selectedGoals).length === 0) {
                setSelectedGoals(defaultGoal);

                return;
            }

            if (!isSelected && selectedGoals[PROMOTION_DEFAULT_GOAL_ID]) {
                const newSelectedGoals = { ...selectedGoals };
                delete newSelectedGoals[PROMOTION_DEFAULT_GOAL_ID];

                setSelectedGoals(newSelectedGoals);
            }
        },
        [selectedGoals],
    );
    const onRemoveGoal = useCallback(
        (key: string) => {
            let newSelectedGoals = { ...selectedGoals };
            delete newSelectedGoals[key];

            if (isRequiredDefaultGoal && Object.keys(newSelectedGoals).length === 0) {
                newSelectedGoals = defaultGoal;
            }

            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals, isRequiredDefaultGoal],
    );
    const onChangeGoalBudget = useCallback(
        (key: string, value: string) => {
            const costValue = Number(value);
            const newSelectedGoals = {
                ...selectedGoals,
                [key]: {
                    cost: costValue,
                    costType: costValue ? DirectGoalCostType.MANUAL : selectedGoals[key].costType,
                },
            };

            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals],
    );

    const [weeklyLimit, setWeeklyLimit] = useState<number | undefined>(metricsSettings.weeklyLimit);
    const onChangeWeeklyLimit = useCallback((value: string) => {
        setWeeklyLimit(Number(value));
    }, []);

    const onChangeGoalCostType = useCallback(
        (key: string, value: DirectGoalCostType) => {
            const newSelectedGoals = {
                ...selectedGoals,
                [key]: {
                    cost: selectedGoals[key].cost,
                    costType: value,
                },
            };

            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals],
    );

    useEffectOnce(() => {
        onLoadMetrics();
    });

    return {
        metrics,
        selectedCounters,
        onSelectCounter,
        onRemoveCounter,
        selectedGoals,
        onSelectGoals,
        onChangeDefaultGoal,
        onRemoveGoal,
        onChangeGoalBudget,
        weeklyLimit,
        onChangeWeeklyLimit,
        onChangeGoalCostType,
    };
};
